import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { Alert, Snackbar } from '@mui/material';

export enum SnackbarType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}
interface SnackBarInput {
  message: string;
  autoHideDuration?: number;
  type?: SnackbarType;
}
interface SnackbarContextType {
  showSnackbar: ({ message, autoHideDuration, type }: SnackBarInput) => void;
  snackbarType: typeof SnackbarType;
}

const defaultSnackbarState = {
  open: false,
  message: '',
  autoHideDuration: 3000,
  vertical: 'top',
  horizontal: 'right',
  type: SnackbarType.SUCCESS || undefined,
};

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbarState, setSnackbarState] = useState(defaultSnackbarState);

  const showSnackbar = useCallback(({ message, autoHideDuration, type }: SnackBarInput) => {
    setSnackbarState({
      ...defaultSnackbarState,
      ...{
        open: true,
        message: message,
        autoHideDuration: autoHideDuration
          ? autoHideDuration
          : defaultSnackbarState.autoHideDuration,
        type: type || defaultSnackbarState.type,
      },
    });
  }, []);

  const handleClose = useCallback(() => {
    setSnackbarState((prevState) => ({ ...prevState, open: false }));
  }, []);

  const contextValue = useMemo(
    () => ({ showSnackbar, snackbarType: SnackbarType }),
    [showSnackbar],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar open={snackbarState.open} autoHideDuration={snackbarState.autoHideDuration} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={snackbarState.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider.');
  }
  return context;
};
